/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getInsuranceClaimItems } from 'app/entities/insurance-claim-item/insurance-claim-item.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { getEntities as getHospitalBills } from 'app/entities/hospital-bill/hospital-bill.reducer';
import { ProcessStatus } from 'app/shared/model/enumerations/process-status.model';
import { createEntity, getEntity, reset, updateEntity } from './provided-item.reducer';

export const ProvidedItemUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const insuranceClaimItems = useAppSelector(state => state.insuranceClaimItem.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const hospitalBills = useAppSelector(state => state.hospitalBill.entities);
  const providedItemEntity = useAppSelector(state => state.providedItem.entity);
  const loading = useAppSelector(state => state.providedItem.loading);
  const updating = useAppSelector(state => state.providedItem.updating);
  const updateSuccess = useAppSelector(state => state.providedItem.updateSuccess);
  const processStatusValues = Object.keys(ProcessStatus);

  const handleClose = () => {
    navigate(`/provided-item${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getInsuranceClaimItems({}));
    dispatch(getGlucoUsers({}));
    dispatch(getHospitalBills({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.price !== undefined && typeof values.price !== 'number') {
      values.price = Number(values.price);
    }
    if (values.dateOfServed !== undefined && typeof values.dateOfServed !== 'number') {
      values.dateOfServed = Number(values.dateOfServed);
    }
    if (values.numberOfConsumptions !== undefined && typeof values.numberOfConsumptions !== 'number') {
      values.numberOfConsumptions = Number(values.numberOfConsumptions);
    }
    values.microSec = convertDateTimeToServer(values.microSec);
    values.timestampEntry = convertDateTimeToServer(values.timestampEntry);
    values.lastUpdated = convertDateTimeToServer(values.lastUpdated);
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...providedItemEntity,
      ...values,
      claimItem: insuranceClaimItems.find(it => it.id.toString() === values.claimItem?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
      bill: hospitalBills.find(it => it.id.toString() === values.bill?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          microSec: displayDefaultDateTime(),
          timestampEntry: displayDefaultDateTime(),
          lastUpdated: displayDefaultDateTime(),
        }
      : {
          status: 'ENTERED',
          ...providedItemEntity,
          microSec: convertDateTimeFromServer(providedItemEntity.microSec),
          timestampEntry: convertDateTimeFromServer(providedItemEntity.timestampEntry),
          lastUpdated: convertDateTimeFromServer(providedItemEntity.lastUpdated),
          claimItem: providedItemEntity?.claimItem?.id,
          patient: providedItemEntity?.patient?.id,
          bill: providedItemEntity?.bill?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.providedItem.home.createOrEditLabel" data-cy="ProvidedItemCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.providedItem.home.createOrEditLabel">Create or edit a ProvidedItem</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="provided-item-id"
                  label={translate('tinyMedicRestApp.providedItem.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.price')}
                id="provided-item-price"
                name="price"
                data-cy="price"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.dateOfServed')}
                id="provided-item-dateOfServed"
                name="dateOfServed"
                data-cy="dateOfServed"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.numberOfConsumptions')}
                id="provided-item-numberOfConsumptions"
                name="numberOfConsumptions"
                data-cy="numberOfConsumptions"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.status')}
                id="provided-item-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {processStatusValues.map(processStatus => (
                  <option value={processStatus} key={processStatus}>
                    {translate(`tinyMedicRestApp.ProcessStatus.${processStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.active')}
                id="provided-item-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.microSec')}
                id="provided-item-microSec"
                name="microSec"
                data-cy="microSec"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.timestampEntry')}
                id="provided-item-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.lastUpdated')}
                id="provided-item-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.providedItem.cursor')}
                id="provided-item-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="provided-item-claimItem"
                name="claimItem"
                data-cy="claimItem"
                label={translate('tinyMedicRestApp.providedItem.claimItem')}
                type="select"
              >
                <option value="" key="0" />
                {insuranceClaimItems
                  ? insuranceClaimItems.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="provided-item-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.providedItem.patient')}
                type="select"
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="provided-item-bill"
                name="bill"
                data-cy="bill"
                label={translate('tinyMedicRestApp.providedItem.bill')}
                type="select"
              >
                <option value="" key="0" />
                {hospitalBills
                  ? hospitalBills.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/provided-item" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProvidedItemUpdate;
